import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit } from '@angular/core';
import * as Highcharts from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { DeviceHealthService } from '../../../shared/services/device-health.service';
import { FacilityService } from '../../../shared/services/facility.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { FloorService } from '../../../shared/services/floor.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NgSelectComponent } from '@ng-select/ng-select';
import { GlobalService } from '../../../shared/services/global.service';

@Component({
  selector: 'app-analytics',
  standalone: true,
  imports: [HighchartsChartComponent, ReactiveFormsModule, CommonModule, FormsModule, NgSelectComponent],
  templateUrl: './analytics.component.html',
  styleUrls: ['./analytics.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AnalyticsComponent implements OnInit {

  facilities: any[] = [];
  floors: any[] = [];
  selectedFacilityId: any = null;
  selectedFloorId: any = null;

  Highcharts: typeof Highcharts = Highcharts;
  alerts: any[] = [];

  startDate: string = '';
  endDate: string = '';
  confirmed = 0;
  unresolved = 0;
  resolved = 0;

  avgResponseTime = '0 min 0 sec';
  avgResolveTime = '0 min 0 sec';
  accuracy = 0;

  // =========================
  // PIE CHART
  // =========================
  pieChartOptions: Highcharts.Options = {
    chart: {
      type: 'pie',
      events: {
        render: function () {
          const chart = this;
          const total = chart.series[0].data.reduce((sum: any, p: any) => sum + p.y, 0);
          if ((chart as any).centerText) {
            (chart as any).centerText.destroy();
          }
          (chart as any).centerText = chart.renderer
            .label(
              `<div style="text-align:center">
                <span style="font-size:22px;font-weight:600">${total}</span><br/>
                <span style="font-size:12px;color:#666">Alerts</span>
              </div>`,
              chart.plotLeft + chart.plotWidth / 2,
              chart.plotTop + chart.plotHeight / 3,
              undefined, undefined, undefined, true
            )
            .attr({ align: 'center', zIndex: 5 })
            .css({ textAlign: 'center' })
            .add();
        }
      }
    },
    title: { text: '' },
    credits: { enabled: false },
    accessibility: { enabled: false },
    legend: {
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
      useHTML: true,
      labelFormatter: function () {
        const p = this as any;
        return `<b>${p.y}</b> ${p.name}`;
      }
    },
    plotOptions: {
      pie: {
        innerSize: '70%',
        dataLabels: { enabled: false },
        showInLegend: true
      }
    },
    series: [{ type: 'pie', data: [] }]
  };

  // =========================
  // BAR CHART
  // =========================
  barChartOptions: Highcharts.Options = {
    chart: { type: 'column' },
    title: { text: '' },
    xAxis: { categories: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] },
    yAxis: { title: { text: '' } },
    credits: { enabled: false },
    legend: { enabled: true },

    series: [ 
        { type: 'column', name: 'Resolved', data: [], color: '#5cb85c', stack: 'alerts' },
        { type: 'column', name: 'Acknowledged', data: [], color: '#f0ad4e', stack: 'alerts' },
        { type: 'column', name: 'NotAcknowledged', data: [], color: '#d9534f', stack: 'alerts' }
    ]
  };

  constructor(
    private alertService: DeviceHealthService,
    private facilityService: FacilityService,
    private spinner: NgxSpinnerService,
    private floorService: FloorService,
    private globalService: GlobalService
  ) {}

  // =========================
  // INIT
  // =========================
  ngOnInit(): void {
    this.loadFacilities();
    this.loadAlerts();
  }

  // =========================
  // LOAD DROPDOWNS
  // =========================
  loadFacilities(): void {
    this.facilityService.getFacilityDropdown({}).subscribe((res: any) => {
      if (res.isSuccess) {
        this.facilities = res.data;
      }
    });
  }

  // =========================
  // DROPDOWN CHANGE HANDLERS
  // =========================
  onFacilityChange(event: any): void {

    const selectedFacility = event;

    if (!selectedFacility?.id) return;

    this.selectedFacilityId = selectedFacility.id;

    // Reset floor when facility changes
    this.selectedFloorId = null;
    this.floors = [];

    const object = {
      facilityId: selectedFacility.id,
      id: null
    };

    this.spinner.show();

    this.floorService.getAllfloor(object).subscribe((res: any) => {

      if (res.isSuccess) {
        this.floors = res.data.list;
      }

      this.spinner.hide();
    });

    // Apply filter only by facility
    this.applyFilters();
  }

  onFloorChange(event: any): void {

    const floor = event;

    if (!floor?.id) return;

    this.selectedFloorId = floor.id;

    this.applyFilters();
  }

  // =========================
  // BUILD & APPLY FILTERS
  // =========================
  applyFilters(): void {
    
    const filters: any = {};

    if (this.selectedFacilityId) {
      filters.facilityId = this.selectedFacilityId;
    }

    if (this.selectedFloorId) {
      filters.floorId = this.selectedFloorId;
    }

    // Date input is YYYY-MM-DD — append time so same-day range covers the full day
    if (this.startDate) {
      filters.startDate = `${this.startDate}T00:00:00`;
    }

    if (this.endDate) {
      filters.endDate = `${this.endDate}T23:59:59`;
    }

    // Pass filters only if at least one is selected; otherwise load all
    this.loadAlerts(Object.keys(filters).length > 0 ? filters : undefined);
  }

  // =========================
  // API CALL
  // =========================
   loadAlerts(filters?: any): void {
    this.spinner.show();

    this.alertService.getAlerts(filters).subscribe({
      next: (res: any) => {

        //Always assign safely
        this.alerts = (res.isSuccess && res.data) ? res.data : [];

        //Process once (no repetition)
        this.processChartData();
        this.processBarChart();
        this.calculateMetrics();

        this.spinner.hide();
      },
      error: () => {
        //Handle error cleanly
        this.alerts = [];

        this.processChartData();
        this.processBarChart();
        this.calculateMetrics();

        this.spinner.hide();
      }
    });
  }

  // =========================
  // PIE CHART DATA
  // =========================
  processChartData(): void {
    this.confirmed = this.alerts.filter(x => x.status === 'NotAcknowledged').length;
    this.unresolved = this.alerts.filter(x => x.status === 'Acknowledged').length;
    this.resolved = this.alerts.filter(x => x.status === 'Resolved').length;


     // Debug: Check if any alert has different status
    const otherStatus = this.alerts.filter(x => 
        x.status !== 'NotAcknowledged' && 
        x.status !== 'Acknowledged' && 
        x.status !== 'Resolved'
    );
    if (otherStatus.length > 0) {
        console.log('Alerts with other status:', otherStatus);
    }
    
    // Make sure total matches
    const pieTotal = this.confirmed + this.unresolved + this.resolved;
    console.log('Alerts length:', this.alerts.length);
    console.log('Pie total:', pieTotal);

    const series = this.pieChartOptions.series as any;
    series[0].data = [
      { name: 'Confirmed infections', y: this.confirmed, color: '#d9534f' },
      { name: 'No infections', y: this.resolved, color: '#5cb85c' },
      { name: 'Unresolved', y: this.unresolved, color: '#f0ad4e' }
    ];
    

    this.pieChartOptions = { ...this.pieChartOptions };
  }

  // =========================
  // BAR CHART DATA
  // =========================
  processBarChart(): void {

    const confirmed = new Array(7).fill(0);   // NotAcknowledged (RED)
    const unresolved = new Array(7).fill(0);  // Acknowledged (YELLOW)
    const resolved = new Array(7).fill(0);    // Resolved (GREEN)

    // Group by local weekday + status — prefer API `date` (UTC) over timeStamp
    this.alerts.forEach(alert => {
      const alertDate =
        this.globalService.parseAlertDate(alert.date) 
        // this.globalService.parseAlertDate(alert.timeStamp);

      if (!alertDate) return;

      const day = alertDate.getDay(); // browser local weekday

      switch (alert.status) {
        case 'NotAcknowledged':
          confirmed[day]++;
          break;

        case 'Acknowledged':
          unresolved[day]++;
          break;

        case 'Resolved':
          resolved[day]++;
          break;
      }
    });

    // Update chart
    this.barChartOptions = {
      chart: { type: 'column' },

      title: { text: '' },

      xAxis: {
        categories: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      },

      yAxis: {
        title: { text: '' }
      },

      credits: { enabled: false },

      legend: { enabled: true },

      // MOST IMPORTANT FIX
      plotOptions: {
        column: {
          stacking: 'percent', // shows ALL colors clearly
          dataLabels: {
            enabled: true,
            formatter: function () {
              const y = this.y;
              return typeof y === 'number' && y > 0 ? y : '';
            }
          }
        }
      },

      tooltip: {
        shared: true,
        pointFormat:
          '<span style="color:{series.color}">●</span> {series.name}: <b>{point.y}</b><br/>'
      },

      //ORDER MATTERS (bottom → top)
      series: [
        {
          type: 'column',
          name: 'Resolved',
          data: resolved,
          color: '#5cb85c',
          stack: 'alerts'
        },
        {
          type: 'column',
          name: 'Acknowledged',
          data: unresolved,
          color: '#f0ad4e',
          stack: 'alerts'
        },
        {
          type: 'column',
          name: 'NotAcknowledged',
          data: confirmed,
          color: '#d9534f',
          stack: 'alerts'
        }
      ]
    };

    // Force Angular refresh
    this.barChartOptions = { ...this.barChartOptions };
  }

  // =========================
  // METRICS
  // =========================
  calculateMetrics(): void {
    const acknowledged = this.alerts.filter(x => x.acknowledgeOn);

    if (acknowledged.length > 0) {
      const total = acknowledged.reduce((sum, item) => {
        return sum + (Date.now() - new Date(item.acknowledgeOn).getTime());
      }, 0);
      this.avgResponseTime = this.formatTime(total / acknowledged.length);
    } else {
      this.avgResponseTime = '0 min 0 sec';
    }

    const resolved = this.alerts.filter(x => x.resolvedOn);

    if (resolved.length > 0) {
      const total = resolved.reduce((sum, item) => {
        const start = new Date(item.acknowledgeOn || item.resolvedOn).getTime();
        const end = new Date(item.resolvedOn).getTime();
        return sum + (end - start);
      }, 0);
      this.avgResolveTime = this.formatTime(total / resolved.length);
    } else {
      this.avgResolveTime = '0 min 0 sec';
    }

    const totalAlerts = this.alerts.length;
    this.accuracy = totalAlerts > 0
      ? Math.round((resolved.length / totalAlerts) * 100)
      : 0;
  }

  // =========================
  // FORMAT TIME
  // =========================
  formatTime(ms: number): string {
    const totalMinutes = Math.floor(ms / 60000);
    if (totalMinutes >= 60) {
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${hours} hr ${minutes} min`;
    }
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${totalMinutes} min ${seconds} sec`;
  }


  // =========================
// HELPER: GET NAMES FOR BADGES
// =========================
getFacilityName(id: any): string {
  const facility = this.facilities.find(f => f.id === id);
  return facility ? facility.name : id;
}

getFloorName(id: any): string {
  const floor = this.floors.find(f => f.id === id);
  return floor ? floor.name : id;
}

// =========================
// CLEAR FILTERS
// =========================
clearFacility(): void {
  this.selectedFacilityId = null;
  this.applyFilters();
}

clearFloor(): void {
  this.selectedFloorId = null;
  this.applyFilters();
}
onDateChange(): void {
  this.applyFilters();
}
clearAllFilters(): void {
  this.selectedFacilityId = null;
  this.selectedFloorId = null;
  this.applyFilters();
}
}


